import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FormContext } from 'src/components/form/form/FormContext';
import { PageWrapper } from 'src/components/form/form/PageWrapper';

import {
  useDataImportValidate,
  useInsertDataImport,
} from '@/hooks/mutations/data-import';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useScoringSettings } from '@/ratings/useScoringSettings';
import type { CreatedEntityDestination } from '@/utils/createWithFiles';
import { dataImportResultsUrl } from '@/utils/urls';

import type { DataImport } from '../../types';
import { visibleTemplateCsvFiles } from '../csvFiles';
import DataImportFormFields from './DataImportFormFields';
import type { DataImportDataFields } from './dataImportSchema';
import { DataImportSchema, defaultValues } from './dataImportSchema';
import { saveDataImportFilesAndValidate } from './saveDataImportFilesAndValidate';
import TemplateLink from './TemplateLink';

export const DataImportForm: FC<{
  dataImport: DataImport | undefined;
}> = ({ dataImport }) => {
  const { updateFiles } = useFileUpdate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const { insertDataImport } = useInsertDataImport();
  const { dataImportValidate } = useDataImportValidate();

  const { t: st } = useTranslation(['common'], { keyPrefix: 'dataImport' });
  const navigate = useNavigate();

  // The impacts template download is gated to multi-impact orgs (mirrors the
  // backend gating in getTemplate.ts).
  const { hasMultipleImpacts } = useScoringSettings({
    configType: 'risk_assessment',
  });

  return (
    <FormContext<DataImportDataFields>
      formId={''}
      header={st('tabTitle')}
      values={
        dataImport
          ? {
              ...dataImport,
              files: dataImport?.files.map((f) => f.file) || [],
            }
          : undefined
      }
      onDismiss={(saved) => {
        if (!saved) {
          navigate('..');
        }
      }}
      defaultValues={defaultValues}
      onSave={async (data) => {
        let dataImportId = dataImport?.Id;
        let createdEntity: CreatedEntityDestination | undefined;
        if (!dataImport) {
          const insertDataImportResult = await insertDataImport();
          dataImportId = insertDataImportResult.insert_data_import_one?.Id;
          if (dataImportId) {
            createdEntity = {
              id: dataImportId,
              destination: dataImportResultsUrl(dataImportId),
            };
          }
        }
        if (!dataImportId) {
          throw new Error('Missing data import id');
        }

        const saveFiles = () =>
          updateFiles({
            parentType: Parent_Type_Enum.DataImport,
            parentId: dataImportId!,
            originalFiles: dataImport?.files.map((f) => f.file),
            selectedFiles: data.files,
          });
        await saveDataImportFilesAndValidate({
          createdEntity,
          saveFiles,
          validate: () => dataImportValidate(dataImportId),
        });

        return {
          savedIds: [dataImportId],
          createdEntity,
          successNavigation: {
            destination: dataImportResultsUrl(dataImportId),
          },
        };
      }}
      schema={DataImportSchema}
      // Bridge this form's save/cancel state up to the page-header object
      // toolbar (CUS-173) — flag-gated so the legacy in-form buttons stay the
      // sole affordance when the toolbar is off.
      publishToolbarState={objectToolbarEnabled}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      i18n={{
        entity_name: st('entity_name'),
      }}
    >
      {visibleTemplateCsvFiles({ hasMultipleImpacts })
        .sort()
        .map((csvFile) => (
          <TemplateLink csvFile={csvFile} key={csvFile} />
        ))}

      <DataImportFormFields />
    </FormContext>
  );
};
